"use client";

import React, { useState } from "react";
import { MapPin, Navigation, CircleCheck, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const serviceAreas = [{
  id: "area-downtown",
  name: "Downtown Plaza",
  zipCodes: "11:00 AM - 2:00 PM",
  x: "45%",
  y: "55%",
  desc: "Catch us by the main fountain for the lunch rush."
}, {
  id: "area-westside",
  name: "Tech Quarter",
  zipCodes: "3:00 PM - 5:00 PM",
  x: "25%",
  y: "40%",
  desc: "Afternoon snacks for the coding crews."
}, {
  id: "area-north",
  name: "Sunset Heights",
  zipCodes: "6:00 PM - 9:00 PM",
  x: "50%",
  y: "20%",
  desc: "Dinner with a view at the park entrance."
}, {
  id: "area-south",
  name: "Harbor Walk",
  zipCodes: "10:00 PM - 12:00 AM",
  x: "35%",
  y: "80%",
  desc: "Late night tacos by the waterfront."
}, {
  id: "area-east",
  name: "Arts District",
  zipCodes: "WEEKEND SPECIAL",
  x: "70%",
  y: "45%",
  desc: "Pop-up location near the street art gallery."
}];
export default function TodaySPitStop() {
  const [activeArea, setActiveArea] = useState<string | null>(null);
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="236715" id="todays-pit-stop" className="bg-background text-foreground py-20 px-4 md:px-8 lg:px-12 overflow-hidden" ref={ref}>
      <div className={cn("max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center transition-all duration-1000", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
        {}
        <div className="relative w-full aspect-square lg:aspect-[4/5] rounded-lg overflow-hidden border-2 border-primary bg-card shadow-3xl group">
          {}
          <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685947/img/rolling_tacos_city_map.png)] bg-cover bg-center opacity-40 grayscale group-hover:opacity-60 transition-opacity duration-700 mix-blend-luminosity" />
          
          {}
          <div className="absolute inset-0 bg-gradient-to-br from-background/90 via-transparent to-background/90 pointer-events-none" />

          {}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ff660012_1px,transparent_1px),linear-gradient(to_bottom,#ff660012_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none" />

          {}
          {serviceAreas.map((area, i) => {
          const isActive = activeArea === area.id;
          return <div key={area.id} data-index={i} className={cn("absolute w-8 h-8 -ml-4 -mt-4 rounded-lg flex items-center justify-center transition-all duration-500 cursor-pointer", isActive ? "scale-125 z-20" : "scale-100 z-10 hover:scale-110", activeArea && !isActive ? "opacity-40" : "opacity-100")} style={{
            left: area.x,
            top: area.y
          }} onMouseEnter={() => setActiveArea(area.id)} onMouseLeave={() => setActiveArea(null)}>
                {}
                <div className={cn("absolute inset-0 rounded-lg animate-ping opacity-75", isActive ? "bg-primary duration-700" : "bg-primary/40 duration-1000")} />
                {}
                <div className={cn("relative w-4 h-4 rounded-sm shadow-lg transition-colors duration-300", isActive ? "bg-primary shadow-[0_0_20px_rgba(255,102,0,0.9)]" : "bg-primary/80")} />
                
                {}
                <div className={cn("absolute top-full mt-2 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-popover text-popover-foreground text-xs font-default font-bold uppercase tracking-wider rounded-md border-2 border-primary shadow-2xl whitespace-nowrap transition-all duration-300 pointer-events-none", isActive ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2")}>
                  {area.name}
                </div>
              </div>;
        })}

          {}
          <div className="absolute bottom-6 left-6 right-6 flex justify-between items-end pointer-events-none">
            <div className="bg-background/90 backdrop-blur-md border-2 border-primary px-4 py-2 rounded-md shadow-xl">
              <p className="text-xs font-mono text-primary flex items-center gap-2 font-bold">
                <Navigation className="w-3 h-3 text-primary animate-pulse" />
                LIVE TRUCK TRACKING: ACTIVE
              </p>
            </div>
          </div>
        </div>

        {}
        <div className="flex flex-col h-full justify-center">
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-1 rounded-md bg-primary text-primary-foreground text-sm font-default font-bold uppercase tracking-widest mb-6">
              <MapPin className="w-4 h-4" />
              Today's Route
            </div>
            <h2 className="font-default text-6xl md:text-7xl font-bold tracking-tighter mb-6 text-foreground uppercase">
              Where We <span className="text-primary">Roll</span>
            </h2>
            <p className="text-xl text-muted-foreground leading-relaxed font-default">
              Rolling Tacos is hitting the streets with fresh heat. Check our live schedule below to find the truck's current pit stop and upcoming locations for the day.
            </p>
          </div>

          <div className="flex-1 min-h-[400px] relative">
            {}
            <div className="absolute top-0 left-0 right-0 h-6 bg-gradient-to-b from-background to-transparent z-10 pointer-events-none" />
            
            <ScrollArea className="h-[400px] pr-4 -mr-4">
              <div className="flex flex-col gap-4 pb-6 pt-2">
                {serviceAreas.map((area, i) => {
                const isActive = activeArea === area.id;
                return <div key={area.id} data-index={i} onMouseEnter={() => setActiveArea(area.id)} onMouseLeave={() => setActiveArea(null)} className={cn("group relative p-6 rounded-md border-2 transition-all duration-300 cursor-default overflow-hidden", isActive ? "bg-primary/10 border-primary shadow-lg shadow-primary/20" : "bg-card border-border hover:border-primary/50")}>
                      {}
                      <div className={cn("absolute inset-0 bg-gradient-to-r from-primary/20 to-transparent opacity-0 transition-opacity duration-300", isActive && "opacity-100")} />
                      
                      <div className="relative z-10 flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-default text-2xl font-bold text-foreground flex items-center gap-2 mb-1 uppercase tracking-tight">
                            {area.name}
                            {isActive && <CircleCheck className="w-5 h-5 text-primary animate-in fade-in zoom-in duration-300" />}
                          </h3>
                          <p className="text-base text-muted-foreground mb-3 font-default">
                            {area.desc}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            <span className="inline-flex items-center px-3 py-1 rounded-md bg-secondary text-sm font-mono text-primary border border-primary/30 font-bold">
                              {area.zipCodes}
                            </span>
                          </div>
                        </div>
                        
                        <div className={cn("w-10 h-10 rounded-md flex items-center justify-center border-2 transition-colors shrink-0", isActive ? "bg-primary text-primary-foreground border-primary" : "bg-muted text-muted-foreground border-border group-hover:border-primary group-hover:text-primary")}>
                          <MapPin className="w-5 h-5" />
                        </div>
                      </div>
                    </div>;
              })}
              </div>
            </ScrollArea>
            
            {}
            <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-background to-transparent z-10 pointer-events-none" />
          </div>

          <div className="mt-8 pt-8 border-t-2 border-border flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm font-mono text-muted-foreground uppercase tracking-wider">
              Want the truck at your event?
            </p>
            <Button asChild size="lg" className="w-full sm:w-auto group bg-primary hover:bg-primary/90 text-primary-foreground font-default font-bold text-xl uppercase tracking-widest rounded-md shadow-lg">
              <Link to="/TruckBookingPage">
                Book The Truck
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}